import * as Form from '@radix-ui/react-form'
import './styles.scss'
import { NumberGridInput } from '../number-grid-input/number-grid-input'
import { useSelector } from 'react-redux'
import { RootState } from '../../app-state/redux-store'
import { Utils } from '@Util/utils'
import { AddOnSelection, MiniGameAddOnSelection, TicketInfoErrors } from '@Config/ticket-form-types'
import { BooleanAddOnCheckbox } from './add-on-checkbox'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import {
  BetExclusionType,
  ExtraDrawsAddOnRuleConfig,
  MiniGameAddOnRuleConfig,
  MultiplierAddOnRuleConfig,
  Range,
} from '@interaction-gaming/game-data-service-browser-client'
import { AddOnTypes } from '@Config/types'
import { MiniGameDrawer } from './mini-game-drawer'
import { MiniGameAddonCheckbox } from './mini-game-addon-checkbox'
import { NumberGridType } from '@Components/number-grid-input/number-grid-input-types'
import { FormErrorMessage } from './form-error-message/error-message'

const CLASS_NAME = 'gby-k2g-ticket-form'

type InputControl<T> = {
  onChange: (newVal: T) => void
  value: T
}

type MiniGameAddOnSelectionWithConfig = MiniGameAddOnSelection & {
  spotOptions: Range
}

export type TicketFormProps = {
  minDrawNumber: number
  maxDrawNumber: number
  firstDrawNumberControl: InputControl<number | null>
  numberOfDrawsControl: InputControl<number>
  costPerDrawControl: InputControl<number>
  numberSelectionsControl: InputControl<number[]>
  addOnSelectionControl: InputControl<AddOnSelection[]>
  errors?: TicketInfoErrors
}

export const TicketForm = (props: TicketFormProps) => {
  const [miniGameSelected, setMiniGameSelected] =
    useState<MiniGameAddOnSelectionWithConfig | null>()
  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)
  const gameConfig = useSelector((state: RootState) => state.gameConfig.gameRules)

  useEffect(() => {
    document.documentElement.style.setProperty('--app-background-color', `#E5E5E5`)
    document.documentElement.style.setProperty('--app-background-glow-color', `#E5E5E5`)
  }, [])

  const findAddonByName = useCallback(
    (addonName: string) => {
      return Array.from(props.addOnSelectionControl.value).find((a) => a.name === addonName)
    },
    [props.addOnSelectionControl],
  )

  const findAddonHostConfigByName = useCallback(
    (addonName: string) => {
      return hostConfig?.addOns.find((a) => a.name === addonName)
    },
    [hostConfig],
  )

  const removeAddonSelection = useCallback(
    (addonName: string) => {
      const newSet = new Set(props.addOnSelectionControl.value)
      const addonToRemove = findAddonByName(addonName)
      newSet.delete(addonToRemove!)

      props.addOnSelectionControl.onChange(Array.from(newSet))
    },
    [findAddonByName, props.addOnSelectionControl],
  )

  const onAddOnSelect = useCallback(
    (selection: AddOnSelection, action: 'ADD' | 'REMOVE') => {
      const newSet = new Set(props.addOnSelectionControl.value)
      const defaultGameConfig = gameConfig?.at(0)

      if (action === 'ADD') {
        if (AddOnTypes.MINI_GAME === selection.type) {
          const miniGameConfig = defaultGameConfig?.supportedAddOns.miniGameAddon
          if (!miniGameConfig) return
          setMiniGameSelected({ ...selection, spotOptions: miniGameConfig.spotOptions })
        } else {
          newSet.add(selection)
          props.addOnSelectionControl.onChange(Array.from(newSet))
        }
      } else {
        removeAddonSelection(selection.name)
      }
    },
    [props.addOnSelectionControl, gameConfig, removeAddonSelection],
  )

  const firstDrawInputHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value) {
        props.firstDrawNumberControl.onChange(null)
        return
      }

      // Who knew that Number('') came out to 0? That's why we have the previous check so we can backspace the whole input properly
      const inputValue = Number(e.target.value)
      if (inputValue <= props.maxDrawNumber && inputValue > 0) {
        props.firstDrawNumberControl.onChange(inputValue)
      }
    },
    [props.firstDrawNumberControl, props.maxDrawNumber],
  )

  if (!gameConfig || gameConfig.length === 0) return <div>Error</div>

  const defaultGameConfig = gameConfig[0]

  let numberOfDrawOptions = Utils.mapRangeWithArrayToArray(defaultGameConfig.supportedDrawRanges)
  const validWagers = defaultGameConfig.baseWagerOptionsInCents.map((wager) => `$${wager / 100}`)

  let maxSelectionsAmount = defaultGameConfig.spotOptions.max ?? 10

  if (defaultGameConfig.baseWagerRestrictions) {
    const wagerRestriction = defaultGameConfig.baseWagerRestrictions.find(
      (val) => val.wagerAmountInCents === props.costPerDrawControl.value,
    )

    if (wagerRestriction) {
      const spotsRule = wagerRestriction.exclusionRules.find(
        (val) => val.type === BetExclusionType.SPOTS,
      )
      if (spotsRule) {
        maxSelectionsAmount = Math.min(...spotsRule.values) - 1
      }

      const numberOfDrawsRule = wagerRestriction.exclusionRules.find(
        (val) => val.type === BetExclusionType.NUMBER_OF_DRAWS,
      )

      if (numberOfDrawsRule) {
        numberOfDrawOptions = numberOfDrawOptions.filter(
          (val) => !numberOfDrawsRule.values.includes(val),
        )
      }
    }
  }

  const mapAddOnToCheckbox = (
    addOn: MultiplierAddOnRuleConfig | ExtraDrawsAddOnRuleConfig | undefined,
    type: AddOnTypes.EXTRA_DRAWS | AddOnTypes.MULTIPLIER | AddOnTypes.MINI_GAME,
  ) => {
    if (!addOn) return null

    let disabled = false

    addOn.exclusionRules.forEach((rule) => {
      switch (rule.type) {
        case BetExclusionType.SPOTS:
          if (rule.values.includes(props.numberSelectionsControl.value.length)) {
            disabled = true

            props.addOnSelectionControl.value.forEach((selection) => {
              if (selection.name === addOn.name) {
                onAddOnSelect(selection, 'REMOVE')
              }
            })
          }
          break
        case BetExclusionType.BET_AMOUNT:
          if (rule.values.includes(props.costPerDrawControl.value)) {
            disabled = true

            props.addOnSelectionControl.value.forEach((selection) => {
              if (selection.name === addOn.name) {
                onAddOnSelect(selection, 'REMOVE')
              }
            })
          }

          break
      }
    })

    return (
      <BooleanAddOnCheckbox
        key={addOn.name}
        type={type}
        disabled={disabled}
        selection={{ ...addOn, iconUrl: findAddonHostConfigByName(addOn.name)?.iconUrl }}
        selected={props.addOnSelectionControl.value.map((a) => a.name).includes(addOn.name)}
        onSelect={(addon, selected) => onAddOnSelect(addon, selected ? 'ADD' : 'REMOVE')}
      />
    )
  }

  const mapMinigameAddon = (addOn: MiniGameAddOnRuleConfig | undefined) => {
    if (!addOn) return null

    const matchingMiniGame = findAddonByName(addOn.name)

    return (
      <MiniGameAddonCheckbox
        key={addOn.name}
        addonConfig={{ ...addOn, iconUrl: findAddonHostConfigByName(addOn.name)?.iconUrl }}
        disabled={false}
        selection={matchingMiniGame as MiniGameAddOnSelection}
        selected={!!matchingMiniGame}
        onSelect={(addon, selected) => onAddOnSelect(addon, selected ? 'ADD' : 'REMOVE')}
        onEdit={(selection) => {
          const miniGameConfig = defaultGameConfig?.supportedAddOns.miniGameAddon
          if (!miniGameConfig) return
          setMiniGameSelected({ ...selection, spotOptions: miniGameConfig.spotOptions })
        }}
      />
    )
  }

  const mapErrorToMessage = (numberOfSelections: number, errors?: TicketInfoErrors) => {
    if (!errors || !Object.values(errors).some((val) => val)) {
      return
    }

    let message = 'Something Went Wrong. Please Try Again.'

    if (props.errors?.startingDrawNumberError) {
      message = `Invalid draw number. Please enter a value between ${props.minDrawNumber} and ${props.maxDrawNumber}.`
    } else if (props.errors?.numberSelectionsError) {
      if (numberOfSelections == 0) {
        return
      }
      message = `Invalid number of spots selected for this wager size. Please select ${maxSelectionsAmount} or less spots.`
    }

    return <FormErrorMessage classPrefix={CLASS_NAME} message={message} />
  }

  return (
    <>
      <div className={CLASS_NAME}>
        <div className={`${CLASS_NAME}__header`} role={'heading'} aria-level={2}>
          {hostConfig?.ticketForm.title ?? 'Keno Numbers'}
        </div>
        <div className={`${CLASS_NAME}__number-selector`}>
          <NumberGridInput
            selections={props.numberSelectionsControl.value}
            onSelectionChange={props.numberSelectionsControl.onChange}
            maxSelections={maxSelectionsAmount}
            gridType={NumberGridType.MAIN}
            selectionError={
              !!props.errors?.numberSelectionsError &&
              props.numberSelectionsControl.value.length > 0
            }
          />
        </div>

        <Form.Root onSubmit={(e) => e.preventDefault()}>
          <div className={`${CLASS_NAME}__ticket-data-section`}>
            <Form.Field className={`${CLASS_NAME}__form-field`} name="draw-number">
              <Form.Label className={`${CLASS_NAME}__form-label`}>
                {hostConfig?.ticketForm.firstDrawNumberLabel ?? 'First Draw #'}{' '}
              </Form.Label>

              <Form.Control asChild>
                <input
                  className={`${CLASS_NAME}__text-input`}
                  type="number"
                  inputMode="numeric"
                  pattern="\d*"
                  value={props.firstDrawNumberControl.value ?? ''}
                  onChange={firstDrawInputHandler}
                  onFocus={(e) => setTimeout(() => e.target.scrollIntoView(), 300)}
                  required
                  min={props.minDrawNumber}
                  max={props.maxDrawNumber}
                  step={1}
                />
              </Form.Control>
            </Form.Field>

            <Form.Field className={`${CLASS_NAME}__form-field`} name="number-of-draws">
              <Form.Label className={`${CLASS_NAME}__form-label`}>
                {hostConfig?.ticketForm.numberOfDrawsLabel ?? '# of Draws'}
              </Form.Label>
              <Form.Control asChild>
                <select
                  className={`${CLASS_NAME}__select-input`}
                  onChange={(e) => {
                    props.numberOfDrawsControl.onChange(Number(e.target.value))
                  }}
                  defaultValue={props.numberOfDrawsControl.value}>
                  {numberOfDrawOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </Form.Control>
            </Form.Field>

            <Form.Field className={`${CLASS_NAME}__form-field`} name="cost-per-draws">
              <Form.Label className={`${CLASS_NAME}__form-label`}>
                {hostConfig?.ticketForm.costPerDrawLabel ?? 'Cost per Draw'}{' '}
              </Form.Label>
              <Form.Control asChild>
                <select
                  className={`${CLASS_NAME}__select-input`}
                  onChange={(e) => props.costPerDrawControl.onChange(Number(e.target.value))}
                  defaultValue={props.costPerDrawControl.value}>
                  {validWagers.map((option, idx) => (
                    <option key={option} value={defaultGameConfig.baseWagerOptionsInCents[idx]}>
                      {option}
                    </option>
                  ))}
                </select>
              </Form.Control>
            </Form.Field>
          </div >
          <hr className={`${CLASS_NAME}__hr`} />
          <div className={`${CLASS_NAME}__add-ons-title-container`}>
            <div className={`${CLASS_NAME}__add-ons-title`} role={'heading'} aria-level={2}>
              {hostConfig?.ticketForm.addOnSectionTitle ?? 'More Ways To Play'}
            </div>
            <div className={`${CLASS_NAME}__add-ons-body-text`}>
              {hostConfig?.ticketForm.addOnSectionBodyText}
            </div>
            <div className={`${CLASS_NAME}__add-ons-container`}>
              <div className={`${CLASS_NAME}__form-addon-field-container`}>
                {mapAddOnToCheckbox(
                  defaultGameConfig.supportedAddOns.multiplierAddon,
                  AddOnTypes.MULTIPLIER,
                )}
                {mapAddOnToCheckbox(
                  defaultGameConfig.supportedAddOns.extraDrawAddon,
                  AddOnTypes.EXTRA_DRAWS,
                )}
              </div>
              <div className={`${CLASS_NAME}__form-addon-mini-game-field-container`}>
                {mapMinigameAddon(defaultGameConfig.supportedAddOns.miniGameAddon)}
              </div>
            </div>
          </div>
        </Form.Root>

        {miniGameSelected ? (
          <MiniGameDrawer
            miniGameName={miniGameSelected.name}
            miniGameIconUrl={findAddonHostConfigByName(miniGameSelected.name)?.iconUrl}
            open={!!miniGameSelected}
            initialNumberSelections={miniGameSelected.numberSelections}
            spotOptions={miniGameSelected.spotOptions}
            onSubmit={(numberSelections) => {
              const newSet = new Set(props.addOnSelectionControl.value)
              const existingMinigame = findAddonByName(miniGameSelected.name)
              if (existingMinigame) {
                newSet.delete(existingMinigame)
              }
              newSet.add({
                ...miniGameSelected,
                numberSelections: [...numberSelections].sort((a, b) => a - b),
              })
              setMiniGameSelected(null)
              props.addOnSelectionControl.onChange(Array.from(newSet))
            }}
            onClose={() => setMiniGameSelected(null)}
          />
        ) : null}
      </div>

      {mapErrorToMessage(props.numberSelectionsControl.value.length, props.errors)}
    </>
  )
}
